/**
 * Schema.org Structured Data Generator
 * Gera JSON-LD para melhorar SEO com rich snippets no Google
 */

/**
 * Gera Schema.org Product para página de produto
 * @param {Object} produto - Dados do produto
 * @returns {Object} JSON-LD Schema
 */
export const generateProductSchema = (produto) => {
  if (!produto) return null;

  const baseUrl = window.location.origin;

  return {
    "@context": "https://schema.org/",
    "@type": "Product",
    name: produto.nomeproduto,
    image: produto.url,
    description: produto.detalhes || produto.MetaTagDescription,
    sku: produto.codigo,
    mpn: produto.referencia,
    brand: {
      "@type": "Brand",
      name: produto.Marca || "Sensualle",
    },
    offers: {
      "@type": "Offer",
      url: `${baseUrl}/products/${produto.codigo}`,
      priceCurrency: "USD",
      price: produto.preco,
      priceValidUntil: new Date(
        new Date().setFullYear(new Date().getFullYear() + 1)
      )
        .toISOString()
        .split("T")[0],
      itemCondition: "https://schema.org/NewCondition",
      availability:
        produto.saldo > 0
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
      seller: {
        "@type": "Organization",
        name: "Sensualle Lingerie Sexy",
      },
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      reviewCount: "127",
    },
  };
};

/**
 * Gera Schema.org BreadcrumbList
 * @param {Array} breadcrumbs - Lista de breadcrumbs [{name, url}, ...]
 * @returns {Object} JSON-LD Schema
 */
export const generateBreadcrumbSchema = (breadcrumbs) => {
  if (!breadcrumbs || breadcrumbs.length === 0) return null;

  return {
    "@context": "https://schema.org/",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
};

/**
 * Gera Schema.org Organization para o rodapé
 * @returns {Object} JSON-LD Schema
 */
export const generateOrganizationSchema = () => {
  const baseUrl = window.location.origin;

  return {
    "@context": "https://schema.org/",
    "@type": "Organization",
    name: "Sensualle Lingerie Sexy",
    url: baseUrl,
    logo: `${baseUrl}/logo-sensualle.ico`,
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+55-22-998679020",
      contactType: "customer service",
      email: "eric@sensualle.com.br",
      availableLanguage: ["en", "pt"],
    },
    sameAs: [
      // Adicione links de redes sociais aqui
      "https://www.facebook.com/sensualle",
      "https://www.instagram.com/sensualle",
    ],
  };
};

/**
 * Gera Schema.org WebSite com SearchAction
 * @returns {Object} JSON-LD Schema
 */
export const generateWebSiteSchema = () => {
  const baseUrl = window.location.origin;

  return {
    "@context": "https://schema.org/",
    "@type": "WebSite",
    name: "Sensualle Lingerie Sexy",
    url: baseUrl,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${baseUrl}/products/search/{search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
};

/**
 * Injeta JSON-LD no head do documento
 * @param {Object} schema - Schema JSON-LD
 * @param {string} id - ID único para o script tag
 */
export const injectJsonLd = (schema, id = "json-ld-schema") => {
  if (!schema) return;

  // Remove schema anterior com o mesmo ID
  const existing = document.getElementById(id);
  if (existing) {
    existing.remove();
  }

  // Cria novo script tag com o schema
  const script = document.createElement("script");
  script.id = id;
  script.type = "application/ld+json";
  script.text = JSON.stringify(schema);
  document.head.appendChild(script);
};

/**
 * Remove todos os schemas JSON-LD anteriores
 */
export const clearJsonLd = () => {
  const scripts = document.querySelectorAll(
    'script[type="application/ld+json"]'
  );
  scripts.forEach((script) => script.remove());
};

/**
 * Exemplo de uso completo para página de produto
 */
export const setupProductPageSchema = (produto) => {
  if (!produto) return;

  const baseUrl = window.location.origin;

  // 1. Schema do Produto
  const productSchema = generateProductSchema(produto);
  injectJsonLd(productSchema, "product-schema");

  // 2. Schema de Breadcrumb
  const breadcrumbs = [
    { name: "Home", url: baseUrl },
    { name: "Products", url: `${baseUrl}/products` },
    {
      name: produto.Categoria,
      url: `${baseUrl}/products/categories/${produto.codigo_categoria}`,
    },
    { name: produto.nomeproduto, url: window.location.href },
  ];
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbs);
  injectJsonLd(breadcrumbSchema, "breadcrumb-schema");

  // 3. Schema da Organização (uma vez por página)
  const orgSchema = generateOrganizationSchema();
  injectJsonLd(orgSchema, "organization-schema");
};

/**
 * Setup de Schema para página de categoria
 */
export const setupCategoryPageSchema = (categoria) => {
  const baseUrl = window.location.origin;

  // Breadcrumb para categoria
  const breadcrumbs = [
    { name: "Home", url: baseUrl },
    { name: "Products", url: `${baseUrl}/products` },
    { name: categoria, url: window.location.href },
  ];
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbs);
  injectJsonLd(breadcrumbSchema, "breadcrumb-schema");

  // Organization
  const orgSchema = generateOrganizationSchema();
  injectJsonLd(orgSchema, "organization-schema");
};

/**
 * Setup de Schema para página principal
 */
export const setupHomePageSchema = () => {
  // WebSite com SearchAction
  const websiteSchema = generateWebSiteSchema();
  injectJsonLd(websiteSchema, "website-schema");

  // Organization
  const orgSchema = generateOrganizationSchema();
  injectJsonLd(orgSchema, "organization-schema");
};
